export const initDocumentAdd = () => {
  const documentForms: NodeListOf<HTMLDivElement> = document.querySelectorAll(".document-form");

  documentForms.forEach((documentForm) => {
    const documentAdd = documentForm.querySelector(".document-add") as HTMLDivElement;
    const buttonDelete = documentForm.querySelector(".button-delete") as HTMLButtonElement;
    const documentSelect = documentForm.querySelector(".document-select") as HTMLDivElement;
    const documentInput = documentForm.querySelector(".document-input") as HTMLInputElement;
    const documentInReview = documentForm.querySelector(".document-in-review") as HTMLDivElement;
    const documentAccepted = documentForm.querySelector(
      ".document-accepted",
    ) as HTMLDivElement | null;
    const documentRejected = documentForm.querySelector(
      ".document-rejected",
    ) as HTMLDivElement | null;

    if (documentSelect && documentInput && documentInReview) {
      // The submit / "Continue" footer lives on the single-document page (outside .document-form); there is only
      // one document form on that page, so a document-level lookup is safe.
      const submitButton = document.querySelector(".document-submit") as HTMLButtonElement | null;
      const submitSpinner = submitButton?.querySelector(".spinner") as HTMLElement | null;

      // The selected-file card (name / size / remove) that replaces the "Add your document" card
      // once a file is chosen.
      const documentFile = documentForm.querySelector(".document-file") as HTMLDivElement | null;
      const documentFileName = documentForm.querySelector(
        ".document-file-name",
      ) as HTMLDivElement | null;
      const documentFileRemove = documentForm.querySelector(
        ".document-file-remove",
      ) as HTMLButtonElement | null;

      let selectedFile: File | null = null;

      const setSubmitting = (isSubmitting: boolean) => {
        if (submitButton) {
          submitButton.disabled = isSubmitting;
        }
        if (submitSpinner) {
          submitSpinner.classList.toggle("d-none", !isSubmitting);
        }
      };

      // Selecting a file no longer uploads immediately: we show it in the file card and let the user
      // confirm with the "Continue" button.
      function selectFile(file: File) {
        if (!file.type.startsWith("image/") && !file.type.startsWith("application/pdf")) {
          alert("Sorry, that's an invalid file type. Only PDF, JPG and PNG allowed.");
          return;
        }

        selectedFile = file;

        // Clear any previous rejection notice now the user is trying a new file.
        if (documentRejected) {
          documentRejected.style.display = "none";
        }

        if (documentFileName) {
          documentFileName.textContent = file.name;
        }

        documentSelect.classList.add("d-none");
        documentFile?.classList.remove("d-none");

        if (submitButton) {
          submitButton.disabled = false;
        }
      }

      function clearFile() {
        selectedFile = null;
        documentInput.value = "";

        documentFile?.classList.add("d-none");
        documentSelect.classList.remove("d-none");

        if (submitButton) {
          submitButton.disabled = true;
        }
      }

      documentFileRemove?.addEventListener("click", clearFile, false);

      if (submitButton) {
        submitButton.addEventListener(
          "click",
          () => {
            if (selectedFile) {
              uploadFile(selectedFile);
            }
          },
          false,
        );
      }

      documentSelect.addEventListener(
        "click",
        () => {
          documentInput.click();
        },
        false,
      );

      documentInput.addEventListener(
        "change",
        (e: Event) => {
          const input = e.currentTarget as HTMLInputElement;

          const fileList = input.files as FileList;

          const file = fileList[0];

          if (file) {
            selectFile(file);
          }
        },
        false,
      );

      documentSelect.addEventListener("dragenter", dragenter, false);
      documentSelect.addEventListener("dragleave", dragleave, false);
      documentSelect.addEventListener("dragover", dragover, false);
      documentSelect.addEventListener("drop", drop, false);

      function dragenter(e: Event) {
        documentSelect.classList.add("active");

        e.stopPropagation();
        e.preventDefault();
      }

      function dragleave(e: Event) {
        documentSelect.classList.remove("active");

        e.stopPropagation();
        e.preventDefault();
      }

      function dragover(e: Event) {
        documentSelect.classList.add("active");

        e.stopPropagation();
        e.preventDefault();
      }

      function drop(e: DragEvent) {
        documentSelect.classList.remove("active");

        e.stopPropagation();
        e.preventDefault();

        const dt = e.dataTransfer as DataTransfer;
        const file = dt.files[0];

        if (file) {
          selectFile(file);
        }
      }

      function uploadFile(file: File) {
        const userId = documentSelect.dataset.userId;

        const uri = `/website-api/users/${userId}/documents/`;
        const xhr = new XMLHttpRequest();
        const fd = new FormData();

        // Loading feedback lives on the "Continue" button (disabled + spinner) for the duration of
        // the upload, mirroring the profile-edit save button.
        setSubmitting(true);

        xhr.onreadystatechange = () => {
          if (xhr.readyState !== 4) {
            return;
          }

          if (xhr.status === 413) {
            // Re-enable the "Continue" button so the user can retry after a failed upload.
            setSubmitting(false);

            alert(
              "Sorry, the file you are trying to add is too large. Upload a file less than 5Mb.",
            );

            return;
          }

          if (xhr.status === 422) {
            setSubmitting(false);

            const jsonResponse = JSON.parse(xhr.response);

            alert(jsonResponse.metadata.errors[0].error);

            return;
          }

          if (xhr.status >= 400) {
            setSubmitting(false);

            const jsonResponse = JSON.parse(xhr.response);

            alert(jsonResponse.metadata.error_message);

            return;
          }

          if (xhr.status === 201) {
            setSubmitting(false);

            const jsonResponse = JSON.parse(xhr.response);
            // VerifyDocument runs synchronously server-side, so the response carries the resulting
            // status: APPROVED, REJECTED, or WAITING_REVIEW (a human will review it).
            const status = jsonResponse.data?.status;

            if (status === "REJECTED") {
              // Show the reason and reset the form so the user can try another file.
              if (documentRejected) {
                const reason = documentRejected.querySelector(
                  ".document-rejected-reason",
                ) as HTMLElement | null;
                if (reason) {
                  reason.textContent = jsonResponse.data?.reject_reason ?? "";
                }
                documentRejected.style.display = "block";
              }

              clearFile();

              return;
            }

            // Approved or awaiting review: hide the add UI, show the outcome, swap "Continue" -> "Done".
            documentAdd.style.display = "none";

            if (status === "APPROVED" && documentAccepted) {
              documentAccepted.style.display = "block";
            } else {
              documentInReview.style.display = "block";
            }

            const documentSubmit = document.querySelector(".document-submit") as HTMLElement | null;
            if (documentSubmit) {
              documentSubmit.style.display = "none";
            }

            const documentDone = document.querySelector(".document-done") as HTMLElement | null;
            if (documentDone) {
              documentDone.style.display = "block";
            }

            buttonDelete.dataset.documentId = jsonResponse.metadata.id;
          }
        };

        xhr.open("POST", uri);
        fd.append("file", file);
        fd.append("type", documentInput.dataset.type as string);
        // Initiate a multipart/form-data upload
        xhr.send(fd);
      }
    }
  });
};
